import createTaskProcessorWorker from "./createTaskProcessorWorker.js";

function clampBetween(v, min, max) {
  if (v <= min)
    v = min;

  if (v >= max)
    v = max;

  return v;
}

function equivalent(a, b) {
  if (Math.abs(a - b) < 0.00001)
    return true;
  else
    return false;
}

function toRadians(d) {
  return d * (Math.PI / 180.0);
}

function magnitude(cartesian) {
  return Math.sqrt(cartesian.x * cartesian.x + cartesian.y * cartesian.y + cartesian.z * cartesian.z);
}

function normalize(cartesian, result) {
  var mag = magnitude(cartesian);

  result.x = cartesian.x / mag;
  result.y = cartesian.y / mag;
  result.z = cartesian.z / mag;
}

function multiplyComponents(left, right, result) {
  result.x = left.x * right.x;
  result.y = left.y * right.y;
  result.z = left.z * right.z;
}

function dot(left, right) {
  return left.x * right.x + left.y * right.y + left.z * right.z;
}

function divideByScalar(cartesian, scalar, result) {
  result.x = cartesian.x / scalar;
  result.y = cartesian.y / scalar;
  result.z = cartesian.z / scalar;
  return result;
};

function multiplyByScalar(cartesian, scalar, result) {
  result.x = cartesian.x * scalar;
  result.y = cartesian.y * scalar;
  result.z = cartesian.z * scalar;
  return result;
};

function add(left, right, result) {
  result.x = left.x + right.x;
  result.y = left.y + right.y;
  result.z = left.z + right.z;
  return result;
};

function subtract(left, right, result) {
  result.x = left.x - right.x;
  result.y = left.y - right.y;
  result.z = left.z - right.z;
  return result;
}

function cross(left, right, result) {

  var leftX = left.x;
  var leftY = left.y;
  var leftZ = left.z;
  var rightX = right.x;
  var rightY = right.y;
  var rightZ = right.z;

  var x = leftY * rightZ - leftZ * rightY;
  var y = leftZ * rightX - leftX * rightZ;
  var z = leftX * rightY - leftY * rightX;

  result.x = x;
  result.y = y;
  result.z = z;
  return result;
};

function clone(left, result) {
  result.x = left.x;
  result.y = left.y;
  result.z = left.z;
  return result;
}

function negate(left, result) {
  result.x = -left.x;
  result.y = -left.y;
  result.z = -left.z;
  return result;
}

function magnitudeSquared(cartesian) {
  return cartesian.x * cartesian.x + cartesian.y * cartesian.y + cartesian.z * cartesian.z;
};

function checkPointVisible(primitive, pos) {
  if (magnitudeSquared(pos) < 1)
    return false;

  var posDir = new Object();
  posDir.x = pos.x;
  posDir.y = pos.y;
  posDir.z = pos.z;
  normalize(posDir, posDir);

  var camDir = new Object();
  camDir.x = primitive._camPosition.x;
  camDir.y = primitive._camPosition.y;
  camDir.z = primitive._camPosition.z;
  normalize(camDir, camDir);

  var d = dot(posDir, camDir);
  d = clampBetween(d, -1, 1);

  var angle = Math.acos(d);

  if (angle > primitive._visibleAngle)
    return false;
  else
    return true;
}

function LLHToCartesian(lon, lat, height) {
  var cartographic = new Object();
  cartographic.longitude = lon * Math.PI / 180.0;
  cartographic.latitude = lat * Math.PI / 180.0;
  cartographic.height = height;

  return cartographicToCartesian(cartographic);
}

function fromTranslation(cartesian, result) {
  result[0] = 1.0;
  result[1] = 0.0;
  result[2] = 0.0;
  result[3] = 0.0;
  result[4] = 0.0;
  result[5] = 1.0;
  result[6] = 0.0;
  result[7] = 0.0;
  result[8] = 0.0;
  result[9] = 0.0;
  result[10] = 1.0;
  result[11] = 0.0;
  result[12] = cartesian.x;
  result[13] = cartesian.y;
  result[14] = cartesian.z;
  result[15] = 1.0;
  return result;
}

function multiplyByVector(matrix, cartesian, result) {
  var vX = cartesian.x;
  var vY = cartesian.y;
  var vZ = cartesian.z;
  var vW = cartesian.w;

  var x = matrix[0] * vX + matrix[4] * vY + matrix[8] * vZ + matrix[12] * vW;
  var y = matrix[1] * vX + matrix[5] * vY + matrix[9] * vZ + matrix[13] * vW;
  var z = matrix[2] * vX + matrix[6] * vY + matrix[10] * vZ + matrix[14] * vW;
  var w = matrix[3] * vX + matrix[7] * vY + matrix[11] * vZ + matrix[15] * vW;

  result.x = x;
  result.y = y;
  result.z = z;
  result.w = w;
  return result;
}

function cartographicToCartesian(cartographic) {
  var result = new Object();

  var n = new Object();
  var k = new Object();

  var longitude = cartographic.longitude;
  var latitude = cartographic.latitude;
  var cosLatitude = Math.cos(latitude);

  var x = cosLatitude * Math.cos(longitude);
  var y = cosLatitude * Math.sin(longitude);
  var z = Math.sin(latitude);

  n.x = x;
  n.y = y;
  n.z = z;
  normalize(n, n);

  var radiiSquared = new Object();
  radiiSquared.x = 6378137.0 * 6378137.0;
  radiiSquared.y = 6378137.0 * 6378137.0;
  radiiSquared.z = 6356752.314145179 * 6356752.314145179;

  multiplyComponents(radiiSquared, n, k);
  var gamma = Math.sqrt(dot(n, k));
  divideByScalar(k, gamma, k);
  multiplyByScalar(n, cartographic.height, n);

  add(k, n, result);

  return result;
}

function random(min, max) {
  var Range = max - min;
  var Rand = Math.random();
  var num = min + Math.round(Rand * Range);
  return num;
}

function randomParticle(primitive, currentParticle) {
  currentParticle.age = Math.round(random(0.0, primitive._numCurrentPoint - 1));

  currentParticle.pos = new Object();
  currentParticle.pos.x = random(0.0, primitive._currentData.depthLength - 1);
  currentParticle.pos.y = random(primitive._currentMinLat, primitive._currentMaxLat);
  currentParticle.pos.z = random(primitive._currentMinLon, primitive._currentMaxLon);

  currentParticle.dir = new Object();

  currentParticle.lastTime = -1;
  currentParticle.firstTime = true;
}

var scratchDir = { x: 0, y: 0, z: 0 };
var scratchUp = { x: 0, y: 0, z: 0 };
var scratchNorth = { x: 0, y: 0, z: 0 };
var scratchRight = { x: 0, y: 0, z: 0, w: 0 };
var scratchLeft = { x: 0, y: 0, z: 0, w: 0 };
var scratchTranslationMatrix = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
var left = { x: 0, y: 0, z: 0, w: 0 };
var right = { x: 0, y: 0, z: 0, w: 0 };
var scratchCartesian3 = { x: 0, y: 0, z: 0 };
var scratchPosition = { x: 0, y: 0, z: 0 };
var scratchPrevPosition = { x: 0, y: 0, z: 0 };
var scratchNextPosition = { x: 0, y: 0, z: 0 };
var tPos = { x: 0, y: 0, z: 0 };

function FeCreateVerticalLine(parameters, transferableObjects) {
  var primitive = event.data;
  var vertexPositions = primitive.positions;
  var width = primitive.lineWidth;
  //垂线
  var finalPositions = primitive.finalPositions;
  var prevPositions = primitive.prevPositions;
  var nextPositions = primitive.nextPositions;
  var expandAndWidth = primitive.expandAndWidth;
  var expandAndWidthIndex = 0;
  var verticalVertexNum = 0;
  var verticalIndexArr = primitive.verticalIndexArray;
  var verticalIndex = 0;
  var verticalIndexNum = 0;
  var finalIndex = 0;
  var prevIndex = 0;
  var nextIndex = 0;

  var positionArr = vertexPositions;
  var count = positionArr.length;
  for (var j = 0; j < count; ++j) {
    var positions = positionArr[j];
    var length = positions.length;
    //剖面顶点
    for (var n = 0; n < length; ++n) {
      var slopePosition = positions[n];
      var worldUpPos = LLHToCartesian(slopePosition[0], slopePosition[1], slopePosition[2]);
      var worldDownPos = LLHToCartesian(slopePosition[0], slopePosition[1], 0.0);
      //垂线
      if (n === 0 || (n === length - 1) || (n % 100) === 0) {
        var size = 2.0 * 4.0 - 4.0;
        //var tPos;
        for (var h = 0; h < 2; ++h) {
          if (h === 0) {
            tPos = scratchCartesian3;
            subtract(worldUpPos, worldDownPos, tPos);
            add(worldUpPos, tPos, tPos);
          } else {
            tPos = worldUpPos;
          }

          clone(tPos, scratchPrevPosition);
          if (h === 0) {
            clone(worldUpPos, scratchPosition);
          } else {
            clone(worldDownPos, scratchPosition);
          }

          if (h === 1) {
            tPos = scratchCartesian3;
            subtract(worldDownPos, worldUpPos, tPos);
            add(worldDownPos, tPos, tPos);
          } else {
            tPos = worldDownPos;
          }
          clone(tPos, scratchNextPosition);

          var startK = h === 0 ? 2 : 0;
          var endK = h === 1 ? 2 : 4;
          for (var k = startK; k < endK; ++k) {
            finalPositions[finalIndex++] = scratchPosition.x;
            finalPositions[finalIndex++] = scratchPosition.y;
            finalPositions[finalIndex++] = scratchPosition.z;

            prevPositions[prevIndex++] = scratchPrevPosition.x;
            prevPositions[prevIndex++] = scratchPrevPosition.y;
            prevPositions[prevIndex++] = scratchPrevPosition.z;

            nextPositions[nextIndex++] = scratchNextPosition.x;
            nextPositions[nextIndex++] = scratchNextPosition.y;
            nextPositions[nextIndex++] = scratchNextPosition.z;

            var direction = (k - 2 < 0) ? -1.0 : 1.0;
            expandAndWidth[expandAndWidthIndex++] = 2 * (k % 2) - 1;
            expandAndWidth[expandAndWidthIndex++] = direction * width;
            verticalVertexNum++;
          }
        }
      }
    }
  }
  //航线信息
  var vertexInfo = new Object();
  //垂线
  var verticalNum = verticalVertexNum / 4;
  for (var v = 0; v < verticalNum; ++v) {
    verticalIndexArr[verticalIndex++] = v * 4 + 0;
    verticalIndexArr[verticalIndex++] = v * 4 + 2;
    verticalIndexArr[verticalIndex++] = v * 4 + 1;

    verticalIndexArr[verticalIndex++] = v * 4 + 1;
    verticalIndexArr[verticalIndex++] = v * 4 + 2;
    verticalIndexArr[verticalIndex++] = v * 4 + 3;
    verticalIndexNum += 6;
  }

  vertexInfo.finalPositions = finalPositions;
  vertexInfo.prevPositions = prevPositions;
  vertexInfo.nextPositions = nextPositions;
  vertexInfo.expandAndWidth = expandAndWidth;
  vertexInfo.verticalIndexArray = verticalIndexArr;
  vertexInfo.verticalIndexNum = verticalIndexNum;
  return vertexInfo;
};
export default createTaskProcessorWorker(FeCreateVerticalLine);